import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  TRACE = 4
}

export interface LoggerConfig {
  globalLevel: LogLevel;
  components: {
    [componentName: string]: LogLevel;
  };
}

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private config: LoggerConfig;
  private defaultConfig: LoggerConfig = {
    globalLevel: environment.production ? LogLevel.WARN : LogLevel.DEBUG,
    components: {
      'settings-panel': LogLevel.WARN,
      'actia-map': LogLevel.INFO,
      'vehicle-details': LogLevel.INFO,
      'side-bar-map': LogLevel.INFO,
      'header': LogLevel.WARN
    }
  };

  constructor() {
    this.loadConfig();
  }

  /**
   * Carica la configurazione dal localStorage o usa quella di default
   */
  private loadConfig(): void {
    try {
      const savedConfig = localStorage.getItem('agri-logger-config');
      if (savedConfig) {
        this.config = { ...this.defaultConfig, ...JSON.parse(savedConfig) };
      } else {
        this.config = this.defaultConfig;
      }
    } catch (error) {
      console.warn('Errore nel caricamento della configurazione logger, uso default:', error);
      this.config = this.defaultConfig;
    }
  }

  /**
   * Salva la configurazione nel localStorage
   */
  public saveConfig(config: Partial<LoggerConfig>): void {
    try {
      this.config = { ...this.config, ...config };
      localStorage.setItem('agri-logger-config', JSON.stringify(this.config));
    } catch (error) {
      console.warn('Errore nel salvataggio della configurazione logger:', error);
    }
  }

  /**
   * Ottiene il livello di log per un componente specifico
   */
  private getLogLevel(component: string): LogLevel {
    return this.config.components[component] ?? this.config.globalLevel;
  }

  /**
   * Verifica se un messaggio deve essere loggato
   */
  private shouldLog(level: LogLevel, component: string): boolean {
    return level <= this.getLogLevel(component);
  }

  /**
   * Formatta il messaggio con timestamp e componente
   */
  private formatMessage(component: string, message: string, level: string): string {
    const timestamp = new Date().toISOString().substr(11, 12);
    return `[${timestamp}] [${level}] [${component}] ${message}`;
  }

  // Metodi di logging pubblici
  public error(component: string, message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR, component)) {
      console.error(this.formatMessage(component, message, 'ERROR'), ...args);
    }
  }

  public warn(component: string, message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN, component)) {
      console.warn(this.formatMessage(component, message, 'WARN'), ...args);
    }
  }

  public info(component: string, message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO, component)) {
      console.info(this.formatMessage(component, message, 'INFO'), ...args);
    }
  }

  public debug(component: string, message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG, component)) {
      console.log(this.formatMessage(component, message, 'DEBUG'), ...args);
    }
  }

  public trace(component: string, message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.TRACE, component)) {
      console.log(this.formatMessage(component, message, 'TRACE'), ...args);
    }
  }

  // Metodi di utilità per configurazione runtime
  public setGlobalLevel(level: LogLevel): void {
    this.saveConfig({ globalLevel: level });
  }

  public setComponentLevel(component: string, level: LogLevel): void {
    this.saveConfig({
      components: {
        ...this.config.components,
        [component]: level
      }
    });
  }

  public getConfig(): LoggerConfig {
    return { ...this.config };
  }

  public resetConfig(): void {
    localStorage.removeItem('agri-logger-config');
    this.loadConfig();
  }

  // Metodi helper per console browser (da usare nelle DevTools)
  public showHelp(): void {
    console.log(`
%c🔧 AGRI LOGGER SERVICE - COMANDI DISPONIBILI:

%c📊 Visualizza configurazione attuale:%c
window.agriLogger.getConfig()

%c🔧 Imposta livello globale:%c
window.agriLogger.setGlobalLevel(3) // 0=ERROR, 1=WARN, 2=INFO, 3=DEBUG, 4=TRACE

%c🎯 Imposta livello per componente:%c
window.agriLogger.setComponentLevel('settings-panel', 4)

%c🔄 Reset configurazione:%c
window.agriLogger.resetConfig()

%c📋 Componenti disponibili:%c
${Object.keys(this.config.components).join(', ')}
    `,
    'color: #3498db; font-weight: bold; font-size: 14px;',
    'color: #2ecc71; font-weight: bold;', 'color: #34495e;',
    'color: #2ecc71; font-weight: bold;', 'color: #34495e;',
    'color: #2ecc71; font-weight: bold;', 'color: #34495e;',
    'color: #2ecc71; font-weight: bold;', 'color: #34495e;',
    'color: #2ecc71; font-weight: bold;', 'color: #34495e;'
    );
  }
}

// Espone il servizio globalmente per uso nelle DevTools
declare global {
  interface Window {
    agriLogger: LoggerService;
  }
}
